/**
 * Safety net — the left-behind-leads lane (docs/crm-safety-net-plan.md §5).
 *
 * Renders GET /api/crm/safety-net: every working lead whose coverage
 * invariant broke (no live next event, or one that sat overdue), grouped one
 * row per lead with a reason chip + evidence line per catch. NETWORK-WIDE on
 * purpose — unlike Today's mine+unassigned scope, this surface exists to see
 * OTHER people's stranded leads (the PTO/departure hole).
 *
 * There is deliberately no dismiss button anywhere on this page: rows leave
 * only when the sweep sees the lead's coverage restored (reply sent, call
 * booked, snooze dated, disposition set) — act on the lead and the row
 * resolves itself. Snooze is offered inline because it IS one of the valid
 * exits; everything else routes through the lead page (rememberLeadOrigin
 * brings the recruiter straight back here).
 *
 * ATTENTION TIERS (backend services/crmAttention.js; owner decision
 * 2026-09-28): the lane lists its leads in two groups. NEEDS ACTION NOW are the
 * catches about a promise broken or someone waiting on us (a reply owed, a
 * missed callback, a stuck review) and a live conversation left hanging; the
 * nav badge and "Work through these" count only these. KEEP TRYING are the rest
 * (an owned lead gone idle, stuck in a stage, ghosted long after they last
 * wrote): listed below, collapsed until opened.
 *
 * Same fetch rhythm as Today: load + 60s silent poll + STATS_CHANGED_EVENT.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCheck, ChevronDown, LifeBuoy, PartyPopper, Undo2, Zap } from 'lucide-react';
import api from '../../services/api';
import { contactName, type ContactListItem } from '../types';
import { STATS_CHANGED_EVENT, notifyStatsChanged, relTime, usePersistedState, useTeam } from '../lib';
import { Alert } from '../components/ui';
import { LifecycleBadge, PageHeader, RefreshButton, SnoozeButton } from '../components';
import { FilterBar, FilterChip, SegmentedControl } from '../components/filters';
import { StudyChips, RowLink, useRowNav } from '../components/shared';
import {
  ackLine,
  detectorChips,
  evidenceLine,
  filterItems,
  splitByTier,
  DETECTOR_LABEL,
  type SafetyNetItem,
  type SafetyNetResponse,
} from './safetyNetLane';
import { useWorkQueue } from '../WorkQueueContext';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const rowCls = 'flex cursor-pointer items-start gap-3 py-3 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20';

const reasonChipCls = 'inline-flex flex-shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
const ownerChipCls = 'inline-flex flex-shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300';

export default function SafetyNet() {
  const navigate = useNavigate();
  const rowNav = useRowNav();
  const { byId } = useTeam();
  const [data, setData] = useState<SafetyNetResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detector, setDetector] = useState('');
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  // 'active' = needs a look; 'acknowledged' = reviewed and parked until the
  // lead's state moves. Parked rows stay READABLE on purpose — an
  // acknowledgement people cannot audit or undo is just a dismiss.
  const [view, setView] = useState<'active' | 'acknowledged'>('active');

  const load = useCallback(async (silent = false, forView?: 'active' | 'acknowledged') => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/api/crm/safety-net', { params: { view: forView || view } });
      setData(res.data as SafetyNetResponse);
      setError(null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => {
    load();
    const silent = () => load(true);
    const timer = window.setInterval(silent, 60_000);
    window.addEventListener(STATS_CHANGED_EVENT, silent);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(STATS_CHANGED_EVENT, silent);
    };
  }, [load]);

  /**
   * "Reviewed — nothing to do until something changes." NOT a dismiss: the
   * server pins the lead's current state, and the sweep puts the row back the
   * moment that state moves (or after the ack window lapses). Optimistic
   * removal from whichever view is on screen.
   *
   * One click, no confirm dialog (owner ask 2026-08-13) — the action is cheap
   * to undo (the "Reviewed" view's put-back button) and self-reverses when the
   * lead moves, so a confirm step bought nothing.
   */
  const setAcknowledged = async (item: SafetyNetItem, acknowledged: boolean) => {
    const c = item.contact;
    if (busyIds.has(c.id)) return;
    setBusyIds((prev) => new Set(prev).add(c.id));
    setData((prev) => prev && ({
      ...prev,
      total: Math.max(0, prev.total - 1),
      acknowledged_total: Math.max(0, (prev.acknowledged_total ?? 0) + (acknowledged ? 1 : -1)),
      items: (prev.items || []).filter((it) => it.contact.id !== c.id),
    }));
    try {
      // One lead can hold several catches; the review covers all of them.
      await Promise.all(item.catches.map((k) =>
        api.patch(`/api/crm/safety-net/${k.id}/acknowledge`, {
          acknowledged, note: null,
        })));
      notifyStatsChanged();
    } catch {
      load(true);
    } finally {
      setBusyIds((prev) => {
        const n = new Set(prev);
        n.delete(c.id);
        return n;
      });
    }
  };

  /** Snooze IS a valid exit (it restores coverage) — optimistic removal. */
  const snooze = async (c: ContactListItem, untilIso: string | null) => {
    if (busyIds.has(c.id) || !untilIso) return;
    setBusyIds((prev) => new Set(prev).add(c.id));
    setData((prev) => prev && ({
      ...prev,
      total: Math.max(0, prev.total - 1),
      items: (prev.items || []).filter((it) => it.contact.id !== c.id),
    }));
    try {
      await api.patch(`/api/crm/contacts/${c.id}`, { snoozed_until: untilIso });
      notifyStatsChanged();
    } catch {
      load(true);
    } finally {
      setBusyIds((prev) => {
        const n = new Set(prev);
        n.delete(c.id);
        return n;
      });
    }
  };

  const chips = useMemo(() => detectorChips(data?.by_detector || {}), [data]);
  const items = useMemo(() => filterItems(data?.items || [], detector), [data, detector]);
  // The two attention groups (the Reviewed view is one list: everything in it is parked).
  const groups = useMemo(() => splitByTier(items), [items]);
  const [keepTryingOpen, setKeepTryingOpen] = usePersistedState<boolean>('safetynet.keeptrying.open', false);
  const nowTotal = data?.tiers ? data.tiers.now : (data?.total ?? null);
  const laterTotal = data?.tiers ? data.tiers.later : 0;
  // With nothing needing action now, the button works the keep-trying catches
  // instead (the "urgent first, then offer the rest" rule, as on Today).
  const workTier: 'now' | 'later' | null = (nowTotal ?? 0) > 0 ? 'now' : laterTotal > 0 ? 'later' : null;

  // "Work through these": the Next-up guided flow, narrowed to the catches
  // (docs/crm-work-queue-plan.md).
  const workQueue = useWorkQueue();

  /** One lead's row: identity, a chip and evidence line per catch, and the lane's actions. */
  const renderItem = ({ contact: c, catches }: SafetyNetItem) => {
    const owner = c.owner_user_id ? byId.get(c.owner_user_id) : null;
    return (
      <div
        key={c.id}
        role="button"
        tabIndex={0}
        {...rowNav(`/crm/contacts/${c.id}`)}
        onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/crm/contacts/${c.id}`); }}
        className={rowCls}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <RowLink
              to={`/crm/contacts/${c.id}`}
              title={contactName(c)}
              className="min-w-0 truncate text-sm font-medium text-gray-900 dark:text-gray-100"
            >
              {contactName(c)}
            </RowLink>
            <LifecycleBadge status={c.lifecycle_status} className="flex-shrink-0" />
            {owner
              ? <span className={ownerChipCls}>{owner.name}</span>
              : <span className={ownerChipCls}>Unassigned</span>}
            {c.last_inbound_at && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                last replied {relTime(c.last_inbound_at)}
              </span>
            )}
          </div>
          <div className="mt-1 space-y-0.5">
            {catches.map((k) => (
              <div key={k.id} className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span className={reasonChipCls}>{DETECTOR_LABEL[k.detector] || k.detector}</span>
                <span className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">
                  {evidenceLine(k)} · caught {relTime(k.first_caught_at)}
                </span>
              </div>
            ))}
            {/* Parked rows say who parked them and when they return —
                an acknowledgement nobody can audit is just a dismiss. */}
            {view === 'acknowledged' && catches[0] && (
              <div className="truncate text-xs italic text-gray-400 dark:text-gray-500">
                {ackLine(catches[0], (id) => byId.get(id)?.name)}
              </div>
            )}
          </div>
          {c.last_comm?.body && (
            <div className="mt-1 truncate text-xs text-gray-400 dark:text-gray-500">
              {c.last_comm.direction === 'inbound' ? '← ' : '→ '}{c.last_comm.body}
            </div>
          )}
          <div className="mt-1">
            <StudyChips studies={c.studies} />
          </div>
        </div>
        <div
          className="flex flex-shrink-0 items-center gap-1"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {view === 'active' ? (
            <>
              <button
                type="button"
                onClick={() => setAcknowledged({ contact: c, catches }, true)}
                disabled={busyIds.has(c.id)}
                title="Reviewed — nothing to do until this lead's situation changes"
                aria-label={`Mark ${contactName(c)} reviewed, no action needed`}
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors duration-150 hover:bg-gray-50 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-primary-400 sm:h-8 sm:w-8"
              >
                <CheckCheck className="h-4 w-4" />
              </button>
              <SnoozeButton
                snoozedUntil={c.snoozed_until}
                busy={busyIds.has(c.id)}
                onSnooze={(untilIso) => snooze(c, untilIso)}
                label={contactName(c)}
              />
            </>
          ) : (
            <button
              type="button"
              onClick={() => setAcknowledged({ contact: c, catches }, false)}
              disabled={busyIds.has(c.id)}
              title="Put this back on the list"
              aria-label={`Put ${contactName(c)} back on the safety-net list`}
              className="inline-flex h-11 items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-8"
            >
              <Undo2 className="h-3.5 w-3.5" />
              Put back
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <PageHeader
        icon={LifeBuoy}
        title="Safety net"
        count={view === 'acknowledged' ? (data?.total ?? null) : nowTotal}
        subtitle="Leads left behind somewhere by accident — every row needs a next step or a disposition, and rows clear themselves once the lead is covered again. The ones that need action now come first."
        actions={
          <>
            {/* [] = network-wide, matching what this lane shows. Without it the
                session would inherit the mine+unassigned default and silently
                work fewer leads than are on screen. */}
            {!workQueue.active && view === 'active' && workTier && (
              <button
                type="button"
                onClick={() => { void workQueue.start('safety_net', [], workTier); }}
                title={workTier === 'now'
                  ? 'Work through the leads that need action now; the rest are offered when those are done'
                  : 'Nothing needs action now: work through the leads worth another try'}
                disabled={workQueue.starting}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 sm:min-h-0"
              >
                <Zap className="h-4 w-4" aria-hidden="true" />
                {workTier === 'now' ? 'Work through these' : 'Work the keep-trying list'}
              </button>
            )}
            <RefreshButton onClick={() => load()} loading={loading} />
          </>
        }
      />

      {error && <Alert tone="error">{error}</Alert>}

      <FilterBar mobileScroll>
        <SegmentedControl
          value={view}
          onChange={(v) => {
            const next = v as 'active' | 'acknowledged';
            setView(next);
            setDetector('');
            load(false, next);
          }}
          options={[
            { value: 'active', label: 'Needs a look' },
            {
              value: 'acknowledged',
              label: `Reviewed${data?.acknowledged_total ? ` (${data.acknowledged_total})` : ''}`,
            },
          ]}
          label="Safety net view"
        />
        {chips.length > 0 && (
          <>
            <FilterChip active={detector === ''} onClick={() => setDetector('')}>
              All
            </FilterChip>
            {chips.map((c) => (
              <FilterChip key={c.key} active={detector === c.key} onClick={() => setDetector(detector === c.key ? '' : c.key)}>
                {c.label} ({c.count})
              </FilterChip>
            ))}
          </>
        )}
      </FilterBar>

      {!loading && data && data.total === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 py-12 text-center dark:border-gray-600">
          <PartyPopper className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          <div className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">
            {view === 'acknowledged' ? 'Nothing parked' : 'Nothing left behind'}
          </div>
          <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
            {view === 'acknowledged'
              ? 'Leads you mark as “no action needed” wait here until their situation changes.'
              : 'Every working lead has a live next step — a reply, a follow-up, a booked call, a snooze, or the agent on it.'}
          </p>
        </div>
      ) : (
        view === 'acknowledged' ? (
          <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="divide-y divide-gray-100 px-4 dark:divide-gray-700">
              {items.map(renderItem)}
              {loading && !data && (
                <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
              )}
            </div>
          </section>
        ) : (
          <div className="space-y-4">
            {groups.now.length > 0 ? (
              <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Needs action now</h2>
                  <span className="text-sm text-gray-500 dark:text-gray-400">({groups.now.length})</span>
                  <span className="ml-auto hidden text-xs text-gray-400 sm:block">A promise broken, someone waiting on us, or a live conversation left hanging</span>
                </div>
                <div className="divide-y divide-gray-100 px-4 dark:divide-gray-700">
                  {groups.now.map(renderItem)}
                </div>
              </section>
            ) : data && groups.later.length > 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                Nothing here needs action now. The leads below are worth another try when you have time.
              </div>
            ) : null}
            {groups.later.length > 0 && (
              <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => setKeepTryingOpen(!keepTryingOpen)}
                  aria-expanded={keepTryingOpen}
                  className="flex w-full items-center gap-2 rounded-xl px-4 py-3 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-gray-700/50"
                >
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Keep trying</h2>
                  <span className="text-sm text-gray-500 dark:text-gray-400">({groups.later.length})</span>
                  <span className="hidden truncate text-xs text-gray-400 sm:block">Idle, stuck in a stage, or quiet long after they last wrote</span>
                  <ChevronDown className={`ml-auto h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-150 ${keepTryingOpen ? 'rotate-180' : ''}`} />
                </button>
                {keepTryingOpen && (
                  <div className="divide-y divide-gray-100 border-t border-gray-100 px-4 dark:divide-gray-700 dark:border-gray-700">
                    {groups.later.map(renderItem)}
                  </div>
                )}
              </section>
            )}
            {loading && !data && (
              <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
            )}
          </div>
        )
      )}
    </div>
  );
}
